import { useEffect } from 'react'
import { Draggable } from '@fullcalendar/interaction'
import { differenceInMinutes, isAfter } from 'date-fns'
import {
  currentTzDate,
  formatTzDate,
  minutesToTimestamp,
  parseLocalDatetimeStringToZonedDate,
  doesEventOverlapAnyOtherEvent,
} from 'Data/format.js'
import { useDataChange, useDataSubmit, useDataValue } from 'Simple/Data.js'
import { UNTEMPLATED_SCHEDULING_ALLOWED_TYPE_OVERLAPS } from 'Data/constants.js'

export default function useEventHandle(props) {
  let submitTimegrid = useDataSubmit({
    context: 'timegrid',
    viewPath: props.viewPath,
  })
  let submitTab = useDataSubmit({
    context: 'tab',
    viewPath: props.viewPath,
  })
  let changeTab = useDataChange({
    context: 'tab',
    viewPath: props.viewPath,
  })
  let timeZoneId = useDataValue({
    context: 'timegrid',
    path: 'location.time_zone_id',
    viewPath: props.viewPath,
  })
  let appointmentQueue = useDataValue({
    context: 'timegrid',
    path: 'appointment_queue',
    viewPath: props.viewPath,
  })
  let draggedAppointment = useDataValue({
    context: 'timegrid',
    path: 'dragged_appointment',
    viewPath: props.viewPath,
  })
  let appointmentTypeCandidates = useDataValue({
    context: 'timegrid',
    path: 'appointment_type_candidates',
    viewPath: props.viewPath,
  })
  let date = useDataValue({
    context: 'timegrid',
    path: 'date',
    viewPath: props.viewPath,
  })

  /**
   * Triggered when the user mouses over an event
   * @param {import('@fullcalendar/core').EventClickArg} info
   */
  function onEventMouseEnter(info) {
    let { event } = info

    switch (event.extendedProps.type) {
      case 'candidate': {
        let api = props.calendarRef.current?.getApi?.()

        let { realEnd, schedulingSlot, selected, description } =
          event.extendedProps

        api?.addEvent({
          id: `${event.id}-shadow`,
          type: 'candidate-shadow',
          start: event.start,
          end: realEnd,
          display: 'background',
          classNames: ['candidate-shadow'],
          resourceId: schedulingSlot.chair_id,
          schedulingSlot,
          selected,
          description,
        })

        break
      }
      default: {
        break
      }
    }
  }

  /**
   * Triggered when the user mouses out of an event
   * @param {import('@fullcalendar/core').EventClickArg} info
   */
  function onEventMouseLeave(info) {
    let { event } = info

    switch (event.extendedProps.type) {
      case 'candidate': {
        let api = props.calendarRef.current?.getApi?.()
        api?.getEventById(`${event.id}-shadow`)?.remove?.()
        break
      }
      default: {
        break
      }
    }
  }

  /**
   * Triggered when resizing stops and the event has changed in duration
   * @param {import('@fullcalendar/interaction').EventResizeDoneArg} value
   */
  function onEventResize({ oldEvent, event, revert }) {
    if (
      event.start === null ||
      event.end === null ||
      oldEvent.start === null ||
      oldEvent.end === null
    )
      return

    let newDuration = differenceInMinutes(event.end, event.start)
    let oldDuration = differenceInMinutes(oldEvent.end, oldEvent.start)

    if (newDuration !== oldDuration && newDuration > 0) {
      switch (event.extendedProps.type) {
        case 'blockTime':
        case 'note': {
          submitTimegrid({
            type: 'updateScheduleNote',
            id: event.id,
            duration: newDuration,
            event_type: event.extendedProps.type,
            revert,
          })

          break
        }
        case 'appointment': {
          submitTimegrid({
            type: 'updateAppointmentDuration',
            id: event.id,
            duration: newDuration,
            revert,
          })
          break
        }
        default: {
        }
      }
    }
  }

  /**
   * Triggered when timegrid event dragging stops and it has moved to a different day/time
   * @param {import('@fullcalendar/core').EventDropArg} value
   */
  function onEventDrop({ event, oldEvent, revert }) {
    let resource = event.getResources()?.[0]
    let oldResource = oldEvent.getResources()?.[0]

    if (
      oldEvent.startStr === event.startStr &&
      resource.id === oldResource.id
    ) {
      return
    }
    switch (event.extendedProps.type) {
      case 'note':
      case 'blockTime': {
        return submitTimegrid({
          type: 'updateScheduleNote',
          id: event.id,
          start_time: event.startStr,
          event_type: event.extendedProps.type,
          chair_id: resource.extendedProps.vaxiom_id,
          revert,
        })
      }
      case 'appointment': {
        setTimeout(
          () =>
            submitTimegrid({
              type: 'rescheduleAppointment',
              appointment_id: event.extendedProps.appointment._id,
              patient_id: event.extendedProps.appointment.patient._id,
              location_id: event.extendedProps.location_id,
              appointment_type_id: event.extendedProps.appointment.type._id,
              start_time: event.startStr,
              end_time: event.endStr,
              chair_id: resource.id,
              revert,
            }),
          300
        )

        break
      }
      default: {
      }
    }
  }

  /**
   * Determines if events being dragged and resized are allowed to overlap each other
   * @type {import('@fullcalendar/core').OverlapFunc}
   */
  function onEventOverlap(stillEvent, movingEvent) {
    if (
      // Any event can overlap with an appointment type slot from topbar filter
      stillEvent.extendedProps.type === 'appointment-type' ||
      stillEvent.extendedProps.type === 'appointment-type-candidate' ||
      // Block time can also be extended under appointments
      movingEvent?.extendedProps.type === 'blockTime' ||
      // and also under notes
      stillEvent?.extendedProps.type === 'note'
    ) {
      return true
    }

    // For now, all the other events can't overlap
    return false
  }

  /**
   * Triggered when event dragging begins
   * @param {import('@fullcalendar/interaction').EventDragStartArg} value
   */
  function onEventDragStart({ event }) {
    let currentDate = currentTzDate(timeZoneId)
    let isNotPastEvent = isAfter(
      parseLocalDatetimeStringToZonedDate(event.endStr, timeZoneId),
      currentDate
    )
    if (event.extendedProps.type === 'appointment' && isNotPastEvent) {
      changeTab(next => {
        next.is_dragging_timegrid_appointment = true
        next.dragged_appointment = {
          id: event.id,
          appointment_type_id: event.extendedProps.appointment.type.id,
          duration: event.extendedProps.booking_duration,
        }
      })
    }
  }

  /**
   * Triggered when timegrid event dragging stops
   * @param {import('@fullcalendar/interaction').EventDragStopArg} value
   */
  function onEventDragStop(value) {
    let { clientX, clientY } = value.jsEvent || {}
    let currentDate = formatTzDate(
      currentTzDate(timeZoneId),
      "yyyy-MM-dd'T'HH:mm:ssXXX",
      timeZoneId
    )

    if (
      value.event.extendedProps.type !== 'appointment' ||
      value.event.endStr <= currentDate ||
      !Number.isFinite(clientX) ||
      !Number.isFinite(clientY)
    ) {
      return
    }

    let dropTarget = document.elementFromPoint(clientX, clientY)
    let parentTarget = dropTarget?.closest('[id]')
    if (parentTarget?.id === 'appointment-queue-target') {
      submitTab({
        type: 'appointmentQueue/add',
        event: {
          ...value.event.extendedProps,
          id: value.event.id,
          start: value.event.start,
          end: value.event.end,
          startStr: value.event.startStr,
          endStr: value.event.endStr,
          title: value.event.title,
          appointment: value.event.extendedProps.appointment,
        },
      })
    } else {
      changeTab(next => {
        next.is_dragging_timegrid_appointment = false
        next.dragged_appointment = null
      })
    }
  }

  /**
   * Programmatic control over where an event can be dropped
   * @type {import('@fullcalendar/core').AllowFunc}
   */
  function onEventAllow(dropInfo, draggedEvent) {
    let currentDate = formatTzDate(
      currentTzDate(timeZoneId),
      "yyyy-MM-dd'T'HH:mm:ssXXX",
      timeZoneId
    )
    let eventEnd = draggedEvent?.endStr || draggedEvent?.extendedProps?.endStr
    return eventEnd > currentDate && dropInfo.endStr > currentDate
  }

  /**
   * Called when an external draggable element with associated event data was dropped onto the timegrid
   * @param {import('@fullcalendar/interaction').EventReceiveArg} value
   */
  function onEventReceive(value) {
    setTimeout(
      () =>
        submitTimegrid({
          id: value.event.id,
          type: 'rescheduleAppointment',
          appointment_id: value.event.extendedProps.appointment?._id,
          patient_id: value.event.extendedProps.appointment?.patient._id,
          location_id: value.event.extendedProps.location_id,
          appointment_type_id: value.event.extendedProps.appointment?.type._id,
          start_time: value.event.startStr,
          end_time: value.event.endStr,
          chair_id: value.event.getResources()?.[0]?.id,
          revert: value.revert,
        }),
      300
    )
  }

  /**
   * Define appointment queue elements as Draggable elements when the list changes
   */
  useEffect(() => {
    if (!appointmentQueue.length) return

    let draggableEl = document.getElementById('appointment-queue')
    let draggable = new Draggable(draggableEl, {
      itemSelector: '.appointment-queue-item',
      eventData: function (eventEl) {
        let viewPath = eventEl.getAttribute('data-view-path')
        let match = viewPath?.match(/\(([^)]+)\)/)
        let id = match ? match[1] : null

        let appt = appointmentQueue.filter(item => item.id === id)?.[0]
        let event = {
          ...appt,
          duration: { minutes: appt.booking_duration },
          create: true,
          startTime: appt.startStr,
        }
        return event
      },
    })

    return () => draggable.destroy()
  }, [appointmentQueue])

  /**
   * Show or remove appointment candidates when timegrid or external appointment drag starts/stops
   */
  useEffect(() => {
    let calendarApi = props.calendarRef.current?.getApi()
    if (calendarApi) {
      if (draggedAppointment) {
        let currentDate = currentTzDate(timeZoneId)
        appointmentTypeCandidates
          .filter(
            item =>
              item.appointment_type_id ===
              draggedAppointment.appointment_type_id
          )
          .map(item => ({
            id: `appointment-type-candidate-${item.appointment_type_id}-${item.start_min}`,
            start: minutesToTimestamp(date, timeZoneId, item.start_min),
            end: minutesToTimestamp(
              date,
              timeZoneId,
              item.start_min + draggedAppointment.duration
            ),
            resourceId: item.resource_id,
          }))
          .forEach(item => {
            let events =
              calendarApi.getResourceById(item.resourceId)?.getEvents() ?? []
            let notAllowedOverlapEvents = events
              .filter(
                item =>
                  !UNTEMPLATED_SCHEDULING_ALLOWED_TYPE_OVERLAPS.includes(
                    item.extendedProps.type
                  )
              )
              .map(item => ({ start: item.startStr, end: item.endStr }))

            if (
              !doesEventOverlapAnyOtherEvent(
                { start: item.start, end: item.end },
                notAllowedOverlapEvents
              ) &&
              isAfter(
                parseLocalDatetimeStringToZonedDate(item.end, timeZoneId),
                currentDate
              )
            ) {
              calendarApi.addEvent({
                ...item,
                type: 'appointment-type-candidate',
                display: 'background',
              })
            }
          })
      } else {
        let candidates = calendarApi
          .getEvents()
          .filter(
            item => item.extendedProps.type === 'appointment-type-candidate'
          )
        candidates.forEach(item => item.remove())
      }
    }
  }, [
    draggedAppointment,
    appointmentTypeCandidates,
    date,
    timeZoneId,
    props.calendarRef,
  ])

  /**
   * Handle appointment drop (including external appointments that do not trigger FullCalendar drop functions)
   */
  useEffect(() => {
    function handleMouseUp() {
      if (draggedAppointment) {
        changeTab(next => {
          next.is_dragging_timegrid_appointment = false
          next.dragged_appointment = null
        })
      }
    }
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [draggedAppointment]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    onEventMouseEnter,
    onEventMouseLeave,
    onEventResize,
    onEventDrop,
    onEventOverlap,
    onEventDragStart,
    onEventDragStop,
    onEventAllow,
    onEventReceive,
  }
}
